import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useFirebase } from '../contexts/FirebaseContext';
import { useAuth } from '../contexts/AuthContext';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { Minus, Plus, Trash2, ShoppingCart } from 'lucide-react';
import { formatCurrencyXCD } from '../config/JadConfig';
import { ChefHeader } from '../components/ios';

const FALLBACK_IMAGE =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"%3E%3Crect fill="%23f3f4f6" width="64" height="64"/%3E%3Ctext fill="%239ca3af" font-family="Arial" font-size="10" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';

// Mirrors KFCStKitts/Views/Customer/CartView.swift — ChefHeader + scrollable
// list of CartItemRow cards, Order Summary card, red "Proceed to Checkout"
// button and destructive-outline "Clear Cart" button on systemGroupedBackground.
function CartPage() {
  const navigate = useNavigate();
  const { db } = useFirebase();
  const {
    items,
    removeFromCart,
    updateQuantity,
    updateSpecialInstructions,
    updateItemBannerURL,
    clearCart,
    getSubtotal,
    getChefServiceFee,
    getDeliveryFee,
    getTotal,
    getItemCount,
    orderType,
    restaurantName,
    restaurantBannerURL,
    deliveryAddress
  } = useCart();

  const { currentUser } = useAuth();
  const [isAppOffline, setIsAppOffline] = useState(false);
  const [isDeliveryEnabled, setIsDeliveryEnabled] = useState(true);
  const fetchedRestaurantIds = useRef(new Set());

  // Calculations
  const subtotal = getSubtotal();
  const chefServiceFee = getChefServiceFee();
  const deliveryFee = getDeliveryFee();
  const total = getTotal();

  // Monitor app offline status and delivery enabled
  useEffect(() => {
    if (db) {
      const settingsRef = doc(db, 'settings', 'global');
      const unsubscribe = onSnapshot(settingsRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setIsAppOffline(data.isAppOffline || false);
          setIsDeliveryEnabled(data.isDeliveryEnabled !== false);
        }
      });
      return unsubscribe;
    }
  }, [db]);

  // Fetch restaurant banner URL for items that don't have it
  useEffect(() => {
    const fetchRestaurantBannerURL = async () => {
      if (!db || items.length === 0) return;

      const itemsWithoutBanner = items.filter(item => !item.restaurantBannerURL);
      if (itemsWithoutBanner.length === 0) return;

      // Get unique restaurant IDs from items without banner
      const restaurantIds = [...new Set(itemsWithoutBanner.map(item => item.restaurantId).filter(Boolean))];

      // Filter out restaurants we've already fetched
      const unfetchedRestaurantIds = restaurantIds.filter(rid => !fetchedRestaurantIds.current.has(rid));
      if (unfetchedRestaurantIds.length === 0) return;

      for (const rid of unfetchedRestaurantIds) {
        try {
          const restaurantRef = doc(db, 'restaurants', rid);
          const restaurantDoc = await getDoc(restaurantRef);
          if (restaurantDoc.exists()) {
            const restaurantData = restaurantDoc.data();
            const bannerURL = restaurantData.bannerURL || restaurantData.menuImageURL;
            if (bannerURL) {
              // Mark this restaurant as fetched
              fetchedRestaurantIds.current.add(rid);
              // Update all items from this restaurant with the banner URL
              itemsWithoutBanner
                .filter(item => item.restaurantId === rid)
                .forEach(item => {
                  updateItemBannerURL(item.id, bannerURL);
                });
            }
          }
        } catch (error) {
          console.error('Error fetching restaurant banner URL:', error);
        }
      }
    };

    fetchRestaurantBannerURL();
  }, [db, updateItemBannerURL]);

  // Handle checkout — guest → login redirect and offline guards preserved.
  // Address selection/validation lives on the checkout page (mirrors iOS,
  // where the cart has no address UI and CheckoutView enforces it).
  const handleCheckout = () => {
    // Check if user is authenticated - required for checkout
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Check if app is offline - blocks all orders
    if (isAppOffline) {
      alert('The app is currently offline. We\'ll be back shortly. Thank you for your patience!');
      return;
    }

    // Check if delivery is disabled - only blocks delivery orders, pickup is allowed
    if (!isDeliveryEnabled && orderType === 'delivery') {
      alert('Delivery is temporarily disabled. Please select pickup instead or check back later.');
      return;
    }

    navigate('/checkout');
  };

  // Mirrors iOS "Clear Cart?" confirmation alert before clearing.
  const handleClearCart = () => {
    if (window.confirm('This will remove all items from your cart.')) {
      clearCart();
    }
  };

  // MARK: - Empty state (mirrors EmptyCartView)
  if (items.length === 0) {
    return (
      <div className="min-h-full bg-[#F2F2F7]">
        <ChefHeader title="Cart" />
        <div className="flex flex-col items-center justify-center px-6 py-24 text-center">
          <ShoppingCart size={60} className="text-[#8E8E93] mb-5" strokeWidth={1.5} />
          <h2 className="text-[22px] font-medium text-gray-900 mb-2">Your cart is empty</h2>
          <p className="text-[15px] text-[#6D6D72] mb-8">Add some delicious items to get started</p>
          <button
            onClick={() => navigate('/menu')}
            className="bg-[#FF3B30] text-white text-[17px] font-semibold px-8 py-3 rounded-xl active:opacity-80 transition-opacity"
          >
            Browse Menu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F2F2F7]">
      <ChefHeader title="Cart" />

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* MARK: - Cart item rows (mirror CartItemRow) */}
        {items.map((item) => (
          <div key={`${item.id}-${item.variantName || ''}`} className="bg-white rounded-xl shadow-sm p-3">
            <div className="flex gap-3">
              {/* Item image — 80x80 rounded-8 like iOS */}
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-black/[0.04] shrink-0">
                <img
                  src={item.menuItemImageURL || item.imageUrl || item.imageURL || item.restaurantBannerURL || (restaurantBannerURL && restaurantBannerURL !== '' ? restaurantBannerURL : FALLBACK_IMAGE)}
                  alt={item.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = item.restaurantBannerURL || (restaurantBannerURL && restaurantBannerURL !== '' ? restaurantBannerURL : FALLBACK_IMAGE);
                  }}
                />
              </div>

              {/* Item details */}
              <div className="flex-1 min-w-0 flex flex-col">
                <h3 className="text-[15px] font-semibold text-gray-900 truncate">{item.name}</h3>
                {item.variantName && (
                  <p className="text-[13px] font-semibold text-[#FF3B30]">Size: {item.variantName}</p>
                )}
                <p className="text-[13px] text-[#8E8E93] line-clamp-2">
                  {item.description || 'Cart item'}
                </p>

                {/* Price + quantity stepper */}
                <div className="flex items-center justify-between mt-auto pt-1">
                  <span className="text-[15px] font-semibold text-gray-900">
                    {formatCurrencyXCD(item.price)}
                  </span>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1, item.variantName || null)}
                      className="w-6 h-6 rounded-full bg-[#FF3B30] text-white flex items-center justify-center active:opacity-70"
                      aria-label="Decrease quantity"
                    >
                      <Minus size={14} strokeWidth={3} />
                    </button>
                    <span className="text-[17px] font-semibold text-gray-900 min-w-[1.25rem] text-center">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1, item.variantName || null)}
                      className="w-6 h-6 rounded-full bg-[#FF3B30] text-white flex items-center justify-center active:opacity-70"
                      aria-label="Increase quantity"
                    >
                      <Plus size={14} strokeWidth={3} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Special instructions + delete (web extras kept, styled as a hairline-separated footer row) */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-black/[0.06]">
              <input
                type="text"
                placeholder="Special instructions..."
                value={item.specialInstructions || ''}
                onChange={(e) => updateSpecialInstructions(item.id, e.target.value, item.variantName || null)}
                className="flex-1 min-w-0 bg-[#F2F2F7] rounded-lg px-3 py-1.5 text-[13px] text-gray-900 placeholder-[#8E8E93] outline-none"
              />
              <button
                onClick={() => removeFromCart(item.id, item.variantName || null)}
                className="w-8 h-8 rounded-full bg-[#FF3B30]/10 text-[#FF3B30] flex items-center justify-center active:opacity-70 shrink-0"
                aria-label="Remove item"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}

        {/* MARK: - Order Summary (mirror iOS summary card) */}
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <h2 className="text-[17px] font-semibold text-gray-900">Order Summary</h2>

          <div className="flex justify-between text-[15px] text-gray-900">
            <span>Subtotal ({getItemCount()} {getItemCount() === 1 ? 'item' : 'items'})</span>
            <span>{formatCurrencyXCD(subtotal)}</span>
          </div>

          {orderType === 'delivery' && deliveryAddress && (
            <div className="flex justify-between text-[15px] text-gray-900">
              <span>Delivery Fee</span>
              <span>{formatCurrencyXCD(deliveryFee)}</span>
            </div>
          )}

          <div className="flex justify-between text-[15px] text-gray-900">
            <span>Chef Service Fee (2%)</span>
            <span>{formatCurrencyXCD(chefServiceFee)}</span>
          </div>

          <div className="border-t border-black/[0.06]" />

          <div className="flex justify-between text-[17px] font-bold text-gray-900">
            <span>Total</span>
            <span>{formatCurrencyXCD(total)}</span>
          </div>
        </div>

        {/* MARK: - Proceed to Checkout (red primary, mirrors iOS Button) */}
        <button
          onClick={handleCheckout}
          className="w-full bg-[#FF3B30] text-white text-[17px] font-semibold py-3.5 rounded-xl active:opacity-80 transition-opacity"
        >
          Proceed to Checkout
        </button>

        {/* MARK: - Clear Cart (destructive outline, mirrors iOS destructive button) */}
        <button
          onClick={handleClearCart}
          className="w-full bg-white text-[#FF3B30] text-[17px] font-semibold py-3.5 rounded-xl border border-[#FF3B30] active:bg-[#FF3B30]/5 transition-colors"
        >
          Clear Cart
        </button>
      </div>
    </div>
  );
}

export default CartPage;
